import type { ReactNode } from 'react'
import { createContext, useCallback, useEffect, useState } from 'react'

import type { User } from '@/types/user'
import { authClient } from '@/lib/auth/custom/client'
import { mapBackendUserToPlatformRole } from '@/lib/auth/role'
import { logger } from '@/lib/default-logger'

import type { UserContextValue } from '../types'

export const UserContext = createContext<UserContextValue | undefined>(undefined)

export interface UserProviderProps {
	children: ReactNode
}

export function UserProvider({ children }: UserProviderProps) {
	const [state, setState] = useState<{ user: User | null; error: string | null; isLoading: boolean }>({
		user: null,
		error: null,
		isLoading: true,
	})

	const checkSession = useCallback(async (): Promise<void> => {
		try {
			const { data, error } = await authClient.getUser()

			if (error) {
				logger.error(error)
				setState((prev) => ({ ...prev, user: null, error: 'Something went wrong', isLoading: false }))
				return
			}

			setState((prev) => ({
				...prev,
				user: data ? ({ ...data, role: mapBackendUserToPlatformRole(data) } as User) : null,
				error: null,
				isLoading: false,
			}))
		} catch (err) {
			logger.error(err)
			setState((prev) => ({ ...prev, user: null, error: 'Something went wrong', isLoading: false }))
		}
	}, [])

	const updateUser = useCallback((user: User) => {
		setState((prev) => ({ ...prev, user }))
	}, [])

	useEffect(() => {
		checkSession().catch((err) => {
			logger.error(err)
			// noop
		})
		// eslint-disable-next-line react-hooks/exhaustive-deps -- Expected
	}, [])

	return <UserContext.Provider value={{ ...state, checkSession, updateUser }}>{children}</UserContext.Provider>
}

export const UserConsumer = UserContext.Consumer
